import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import Navigation from '../../Components/Navigation'
import './ApproveSaleOrders.css'
import { CustomSwitch } from 'Components/Switch/CustomSwitch.jsx'
import { documentTypeId, VAPermissions } from 'Utils/constants'
import {
  searchSalesDocumentDetailService,
  AddUpdateSalesDocumentsService,
  UpdateApprovalSalesOrder,
} from 'redux/services/cotizacionServices'
import No from '../../Assets/Images/Login/no.svg'
import CheckIcon from '../../Assets/Images/Login/check.png'
import MiniLoading from 'Components/MiniLoading'
import { CircularProgress } from '@mui/material'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import Box from '@mui/material/Box'
import useValidatePermission from 'Hooks/useValidatePermission'
import { t } from 'i18next'
import CostMarginVisibility from 'Components/CostMarginVisibility/CostMarginVisibility'
import moment from 'moment'
import { getProductPriceInventoryService } from 'redux/services/clientsServices'

const ApproveSaleOrders = () => {
  const { saleOrderDocHeaderId, saleOrderDocumentNumber } = useParams()
  const [odvProducts, setOdvProducts] = useState([])
  const [allOdvData, setAllOdvData] = useState([])
  const [loading, setLoading] = useState(true)
  const { user } = useSelector(state => state.login)
  const seeCostProd = useValidatePermission(VAPermissions.seeCostProd)
  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const [sendingSubmit, setSendingSubmit] = useState(false)
  const [showModalPriceChangeApproval, setShowModalPriceChangeApproval] =
    useState(false)
  const approveDiscount = useValidatePermission(VAPermissions.aproveDiscount)
  const canChangePriceODV = useValidatePermission(
    VAPermissions.priceOverrideAuthODV
  )
  const [checkedProductsCount, setCheckedProductsCount] = useState(0)

  const canApproveSaleOrder = () => {
    if (!allOdvData || !user?.AuthenticationInfo?.UserId) {
      return false
    }

    return canChangePriceODV
  }

  const getPriceInventory = async (
    PriceProducts,
    CompanyId,
    CompanyCode,
    WarehouseId
  ) => {
    const payload = {
      CompanyId,
      CompanyCode,
      WarehouseId,
      ProductIds: PriceProducts || [],
      PriceListId: null,
      UoMId: null,
    }

    try {
      const response = await getProductPriceInventoryService(payload)
      const { data, status } = response
      if (status === 200 && data?.Data) {
        return data.Data
      } else {
        return null
      }
    } catch (err) {
      return null
    }
  }

  const getProductPrices = async (
    productsData,
    CompanyId,
    CompanyCode,
    WarehouseId
  ) => {
    const productIds = productsData.map(item => item.ProductId)
    const responsePriceInventory = await getPriceInventory(
      productIds,
      CompanyId,
      CompanyCode,
      WarehouseId
    )

    const productsWithPrices = productsData.map(product => {
      const priceInfo = responsePriceInventory?.find(
        priceItem => priceItem.ProductId === product.ProductId
      )
      return {
        ...product,
        Cost: priceInfo?.Cost || priceInfo?.AvgCost || 0,
      }
    })
    return productsWithPrices
  }

  const fetchDocumentODV = useCallback(async () => {
    const payload = {
      DocumentTypeId: documentTypeId.ODV,
      DocumentHeaderId: saleOrderDocHeaderId,
      DocumentNumber: saleOrderDocumentNumber,
      PageNumber: 1,
      PageSize: 1,
    }

    try {
      const response = await searchSalesDocumentDetailService(payload)
      const addSalesOrderLineId = response?.data?.Data?.Lines.map(prod => ({
        ...prod,
        SalesOrderLineId: prod.DocLineId,
      }))
      setAllOdvData({
        ...response?.data?.Data,
        Lines: addSalesOrderLineId,
      })

      const productsNeedAuth = addSalesOrderLineId.filter(
        prod =>
          prod.Price < prod.FinalPrice &&
          prod.AuthorizedPriceBy === null &&
          prod.AuthorizedPriceByName === null
      )
      const productsWithIsChecked = productsNeedAuth.map(product => ({
        ...product,
        isChecked: false,
      }))

      const productsWithPrices = await getProductPrices(
        productsWithIsChecked,
        response?.data?.Data?.CompanyId,
        response?.data?.Data?.CompanyCode,
        response?.data?.Data?.WarehouseId
      )
      setOdvProducts(productsWithPrices)
      setLoading(false)
    } catch (error) {
      console.error('Error to get document detail', error)
    }
  }, [])

  useEffect(() => {
    fetchDocumentODV().then()
  }, [saleOrderDocumentNumber])

  const isAllChecked = !odvProducts.some(items => items?.isChecked !== true)

  const handleAllSwitchChange = state => {
    setOdvProducts(products =>
      products.map(prod => ({
        ...prod,
        isChecked: state,
      }))
    )
    setCheckedProductsCount(state ? odvProducts.length : 0)
  }

  const handleSwitchChange = productId => {
    setOdvProducts(products => {
      const updatedProducts = products.map(prod =>
        prod.SystemCode === productId
          ? { ...prod, isChecked: !prod.isChecked }
          : prod
      )
      const newCount = updatedProducts.filter(prod => prod.isChecked).length
      setCheckedProductsCount(newCount)
      return updatedProducts
    })
  }

  const updateProductsAuth = async () => {
    setSendingSubmit(true)

    const prodsUpdated = odvProducts?.map(item => {
      if (item?.isChecked) {
        return {
          ...item,
          AuthorizedPriceBy: user?.AuthenticationInfo?.UserId,
          AuthorizedPriceByName: user?.AuthenticationInfo?.UserName,
        }
      } else {
        return item
      }
    })

    const updatedProducts = allOdvData?.Lines?.map(originalProduct => {
      const productFiltered = prodsUpdated.find(
        item => item.SystemCode === originalProduct.SystemCode
      )
      return productFiltered
        ? { ...originalProduct, ...productFiltered }
        : originalProduct
    })

    const payload = {
      DocumentTypeId: documentTypeId.ODV,
      SalesOrder: {
        SalesOrderHeaderId: saleOrderDocHeaderId,
        ...allOdvData,
        Lines: updatedProducts,
      },
    }
    try {
      const response = await AddUpdateSalesDocumentsService(payload)
      const documentHeaderID =
        response?.data?.Data?.SalesDocumentInfo?.DocHeaderId || ''

      if (documentHeaderID) {
        await UpdateApprovalSalesOrder({
          DocumentHeaderId: documentHeaderID,
          Approve: true,
        })
      }

      setShowModalPriceChangeApproval(true)
    } catch (error) {
      console.error('Error to try update product and documents:', error)
    } finally {
      setSendingSubmit(false)
    }
  }

  function handleCloseModalApprove() {
    setShowModalPriceChangeApproval(false)
    setTimeout(() => {
      history.back()
    }, 2000)
  }

  return (
    <>
      <Navigation />
      <section className="approved-main">
        <div className="approved-header">
          <h2>{t('APPROVE_SALE_ORDERS.PAGE_TITLE')}</h2>
        </div>
        {loading ? (
          <MiniLoading />
        ) : (
          <>
            <Modal show={showModalPriceChangeApproval} centered>
              <Box>
                <div className="approve-modal-box-message">
                  <div className="closing-button">
                    <img
                      src={No}
                      onClick={handleCloseModalApprove}
                      alt="close"
                    />
                  </div>
                  <div className="modal-box-channel-message-content">
                    <img src={CheckIcon} alt="check" />
                    <h2>
                      El precio ha sido
                      <br />
                      cambiado exitosamente
                    </h2>
                  </div>
                </div>
              </Box>
            </Modal>
            <div style={{ backgroundColor: 'var(--main-color)' }}>
              <div className="space-between">
                <p className="header-info-odv">
                  Orden N°: {saleOrderDocumentNumber}
                </p>
                <p className="header-info-odv">
                  Cliente: {allOdvData?.CustomerFullName}
                </p>
                <p className="header-info-odv">
                  Vendedor: {allOdvData?.SalesAgentFullName}
                </p>
                <p className="header-info-odv">
                  Fecha de creación:{' '}
                  {moment(allOdvData?.CreatedDate).format('DD/MM/YYYY h:mm A')}
                </p>
              </div>

              <p className="importantMessage">
                <span style={{ fontWeight: 'bold' }}>Importante: </span>
                {t('APPROVE_SALE_ORDERS.IMPORTANT_MESSAGE')}
              </p>
            </div>
            <div className="general-container">
              <div className="table-container-approve">
                <table className="inventory-table-approve">
                  <thead className="table-header-approve">
                    <tr>
                      <th>Referencia</th>
                      <th>Descripción</th>
                      <th>Precio inicial</th>
                      <th>Precio negociado</th>
                      {seeCostProd ? <th>Costo</th> : null}
                      {seeMargenProd ? <th>Margen de Ganancia</th> : null}
                      <th>
                        <span style={{ marginRight: 12 }}>Acciones</span>
                        <CustomSwitch
                          color="#11D29E"
                          checked={isAllChecked}
                          onChange={() => handleAllSwitchChange(!isAllChecked)}
                          disabled={!canApproveSaleOrder()}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {odvProducts?.map((product, index) => (
                      <tr
                        className="inventory-table-aprove-row inventory-table-aprove-col"
                        key={product?.SystemCode || ''}>
                        <td>{product?.SystemCode || ''}</td>
                        <td>{product?.ProductName || ''}</td>
                        <td>${product?.BasePrice || ''}</td>
                        <td>${product?.Price?.toFixed(2) || ''}</td>
                        {seeCostProd ? (
                          <td>
                            <CostMarginVisibility
                              seeCostProd
                              simpleCost
                              isCost
                              cost={product?.Cost}
                              centerInTable
                            />
                          </td>
                        ) : null}
                        {seeMargenProd ? (
                          <td>
                            <CostMarginVisibility
                              seeMargenProd={true}
                              simple
                              isMargin
                              margin={(
                                ((product.Price - product.Cost) /
                                  product.Price) *
                                100
                              ).toFixed(2)}
                              centerInTable
                            />
                          </td>
                        ) : null}
                        <td>
                          <div>
                            <CustomSwitch
                              color="#11D29E"
                              inputProps={{
                                'data-testid': `switch-approve-product-${product.SystemCode}`,
                              }}
                              checked={product.isChecked || false}
                              onChange={isChecked =>
                                handleSwitchChange(product.SystemCode)
                              }
                              disabled={!canApproveSaleOrder()}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="approve-save-button">
              <button
                className={
                  approveDiscount &&
                  checkedProductsCount > 0 &&
                  canApproveSaleOrder()
                    ? 'save_button'
                    : 'disabled'
                }
                disabled={
                  !approveDiscount ||
                  checkedProductsCount === 0 ||
                  !canApproveSaleOrder()
                }
                onClick={updateProductsAuth}>
                {sendingSubmit ? (
                  <CircularProgress size={20} />
                ) : (
                  <p>Guardar</p>
                )}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default ApproveSaleOrders
